import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { FirFormRegenService } from './fir-form-regen.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-fir-form-regen',
    templateUrl: './fir-form-regen.component.html',
    styleUrls: ['./fir-form-regen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirFormRegenComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  logged_in_user: string = '';
  rowData: any = {};
  gridDataMultipleSelect: any[] = [];
  isSaveClicked = false;
  loading = false;
  processing: boolean = false;
  showFormLoader = true;
  showConfirmModal = true;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to regenerate form data for the selected entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };
  taxYears: any[] = [];
  selectedTaxYear: any = null;
  selectedScenario: any = null;
  consolGroups: any[] = [];
  selectedConsolGroup: any = null;
  forms: string[] = ['5471', '8858', '8865', '8990', '8992'];
  selectedName = '';
  private isInitialized = false;
  private isLoading = false;
  private loadSeq = 0;
  /** Bumped on workspace/global context reset and destroy; ignores stale `generate()` HTTP callbacks. */
  private generateSeq = 0;

  private readonly destroyed$ = new Subject<void>();
  private readonly contextSubs = new Subscription();

  constructor(
    private firFormRegenService: FirFormRegenService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.loadSeq++;
    this.generateSeq++;
    this.contextSubs.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc as GtwElementsBaseUrlsModel;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    // Get batch-selected grid data (multi-select rows) for form regeneration
    const gridSelection = gridPayloadFromActiveScreen(activeScreen);
    this.gridDataMultipleSelect = Array.isArray(gridSelection) ? gridSelection : (gridSelection ? [gridSelection] : []);
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || this.globalService?.sso_id || '';
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    return (
      this.GENERAL_CONFIG?.base_url ||
      this.baseURLs?.api ||
      this.baseURLs?.admin ||
      ''
    ).toString();
  }

  private resetAndTryInit(): void {
    this.isInitialized = false;
    this.isLoading = false;
    this.loadSeq++;
    this.generateSeq++;
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.resolveApiBase()) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.taxYears = this.buildTaxYears();
    if (this.taxYears.length > 0) {
      this.selectedTaxYear = this.taxYears[0];
      this.selectedScenario = this.selectedTaxYear?.scenarios?.[0] || null;
    }
    this.loadFilingGroups();
  }

  private buildTaxYears(): any[] {
    const gp = extractObject(this.globalService?.globalParams);
    const inputYears =
      this.USER_SETTINGS?.taxYears || this.globalService?.taxYears || gp.taxYears || this.filterParams?.taxYears || [];
    if (Array.isArray(inputYears) && inputYears.length > 0) {
      return inputYears;
    }
    const year = this.rowData?.TAX_YEAR || this.filterParams?.tax_year || gp.tax_year || this.globalService?.tax_year;
    const scenario =
      this.rowData?.SCENARIO || this.filterParams?.scenario || gp.scenario || this.globalService?.scenario;
    return year ? [{ label: String(year), value: year, scenarios: [{ scenario, scenario_desc: String(scenario || '') }] }] : [];
  }

  changeTaxYear(): void {
    this.selectedScenario = this.selectedTaxYear?.scenarios?.[0] || null;
    this.loadFilingGroups();
  }

  changeScenario(): void {
    this.loadFilingGroups();
  }

  private loadFilingGroups(): void {
    const taxYear = this.selectedTaxYear?.value || this.filterParams?.tax_year || this.rowData?.TAX_YEAR;
    const scenario = this.selectedScenario?.scenario || this.filterParams?.scenario || this.rowData?.SCENARIO;
    const jcdKey =
      this.rowData?.JCD_KEY ||
      this.filterParams?.jcd_key ||
      this.globalService?.globalParams?.jcd_key ||
      this.globalService?.jcd_key ||
      250;
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;
    const seq = ++this.loadSeq;
    this.firFormRegenService.getFilingGroups(baseUrl, taxYear, scenario, jcdKey).pipe(takeUntil(this.destroyed$)).subscribe(
      (data: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.consolGroups = data?.jsonObject || [];
        this.selectedConsolGroup = this.consolGroups[0] || null;
        this.isLoading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      () => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.consolGroups = [];
        this.selectedConsolGroup = null;
        this.isLoading = false;
        this.showFormLoader = false;
        this.gtwSnackbarService.openSnackBar('Unable to load consol groups', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  generate(): void {
    if (this.processing) {
      return;
    }
    const taxYear = this.selectedTaxYear?.value || this.filterParams?.tax_year || this.rowData?.TAX_YEAR;
    const scenario = this.selectedScenario?.scenario || this.filterParams?.scenario || this.rowData?.SCENARIO;
    const filingKey = this.selectedConsolGroup?.VALUE;
    const formNo = this.selectedName;
    if (!taxYear || !scenario || !filingKey || !formNo) {
      this.gtwSnackbarService.openSnackBar('Please select all required fields', 'gtw-snackbar--yellow');
      return;
    }
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onConfirmGenerate(): void {
    this.showConfirmModal = false;
    const baseUrl = this.resolveApiBase();
    const genEpoch = ++this.generateSeq;

    // Build grid selection data matching legacy syncSaveDtls structure
    const gridData = this.gridDataMultipleSelect.length > 0 ? this.gridDataMultipleSelect : (this.rowData ? [this.rowData] : []);
    const syncSaveDtls: any[] = [];
    let combinationKeys = '';

    gridData.forEach((row: any) => {
      const r = row?.data || row;
      if (r?.COMBINATION_KEY) {
        syncSaveDtls.push({
          object_id: r.object_id,
          combination_key: r.COMBINATION_KEY,
          leid: r.LEID,
          cdr_number: r.CDR_NO,
          reporting_period: r.REPORTING_PERIOD,
          scenario_key: r.SCENARIO_KEY || this.filterParams?.scenario
        });
      }
    });
    combinationKeys = syncSaveDtls.map(item => item.combination_key).join(',');

    // Get irs_form_no from screen filters (matching legacy: TriggerFormGenFactory.getScreenFilters().irs_form_no)
    const formNo = this.filterParams?.irs_form_no || this.selectedName || this.rowData?.FORM || this.rowData?.FORM_NO || '';

    // Build clobSettings matching legacy returnClobSettingsObj
    const clobSettings: any = {
      sso_id: this.logged_in_user,
      combination_key: combinationKeys,
      irs_form_no: formNo
    };

    // Build clobData from grid selection (legacy syncSaveDtls passed as Details)
    const clobData: any[] = syncSaveDtls;

    // Get globalParams and filterParams
    const globalParams = this.globalService?.globalParams || {};
    const filterParams = this.filterParams || {};

    this.processing = true;
    this.loading = true;
    this.cdr.detectChanges();

    this.firFormRegenService
      .triggerFormGeneration(baseUrl, clobSettings, clobData, globalParams, filterParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data: any) => {
          if (genEpoch !== this.generateSeq) {
            this.processing = false;
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.processing = false;
          this.loading = false;
          if (data?.errorMessage && data.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Form Data will be regenerated for selected entities', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          if (genEpoch !== this.generateSeq) {
            this.processing = false;
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.processing = false;
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while generating form', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  onCancelGenerate(): void {
    this.showConfirmModal = false;
    this.cancel();
  }

  trackByTaxYear(_index: number, item: any): any {
    return item?.value;
  }

  trackByConsol(_index: number, item: any): any {
    return item?.VALUE;
  }

  trackByForm(_index: number, item: string): any {
    return item;
  }

  trackByScenario(_index: number, item: any): any {
    return item?.scenario;
  }

  get scenarios(): any[] {
    if (!this.selectedTaxYear || !Array.isArray(this.selectedTaxYear.scenarios)) {
      return [];
    }
    return this.selectedTaxYear.scenarios;
  }
} 
